import React, { useState } from 'react';
import Icon from '../../../AppIcon';
import Button from '../../../ui/Button';
import Input from '../../../ui/Input';

const PayoutManagement = ({ payoutData }) => {
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [payoutMethod, setPayoutMethod] = useState('bank_transfer');

  const payoutMethods = [
    { 
      value: 'bank_transfer', 
      label: 'Bank Transfer', 
      icon: 'CreditCard',
      description: '2-3 business days',
      fee: 'Free'
    },
    { 
      value: 'paypal', 
      label: 'PayPal', 
      icon: 'Wallet',
      description: 'Instant transfer',
      fee: '2.9% + $0.30'
    },
    { 
      value: 'crypto', 
      label: 'Cryptocurrency', 
      icon: 'Bitcoin',
      description: '1-2 hours',
      fee: 'Network fees apply'
    },
    { 
      value: 'check', 
      label: 'Physical Check', 
      icon: 'FileText',
      description: '7-10 business days',
      fee: '$5.00'
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: 'Clock' },
      processing: { color: 'bg-blue-100 text-blue-800', icon: 'Loader' },
      completed: { color: 'bg-green-100 text-green-800', icon: 'CheckCircle' },
      failed: { color: 'bg-red-100 text-red-800', icon: 'XCircle' },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: 'X' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} className="mr-1" />
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const totalAvailable = payoutData?.availableBalance;
  const totalPending = payoutData?.pendingPayouts?.reduce((sum, payout) => sum + payout?.amount, 0);
  const nextPayoutDate = "November 30, 2024";

  return (
    <div className="space-y-6">
      {/* Balance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Icon name="DollarSign" size={24} className="text-green-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-800">${totalAvailable?.toLocaleString()}</div>
              <div className="text-sm text-green-600">Available Balance</div>
            </div>
          </div>
          <Button variant="default" fullWidth>
            <Icon name="ArrowDown" size={16} className="mr-2" />
            Request Payout
          </Button>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Icon name="Clock" size={24} className="text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-800">${totalPending?.toLocaleString()}</div>
              <div className="text-sm text-blue-600">Pending Payouts</div>
            </div>
          </div>
          <div className="text-sm text-blue-700">
            Next payout: {nextPayoutDate}
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Icon name="TrendingUp" size={24} className="text-purple-600" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-purple-800">${payoutData?.monthlyEarnings?.toLocaleString()}</div>
              <div className="text-sm text-purple-600">This Month</div>
            </div>
          </div>
          <div className="flex items-center text-sm text-purple-700">
            <Icon name="ArrowUp" size={14} className="mr-1" />
            +23% from last month
          </div>
        </div>
      </div>
      {/* Payout Methods */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Payout Methods</h3>
            <p className="text-sm text-muted-foreground">Choose your preferred payout method</p>
          </div>
          <Button variant="outline" size="sm">
            <Icon name="Plus" size={16} className="mr-2" />
            Add Method
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {payoutMethods?.map((method) => (
            <div 
              key={method?.value}
              onClick={() => setPayoutMethod(method?.value)}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                payoutMethod === method?.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/50'
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className={`p-2 rounded-lg ${payoutMethod === method?.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                  <Icon name={method?.icon} size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-foreground">{method?.label}</h4>
                    {payoutMethod === method?.value && (
                      <Icon name="CheckCircle" size={16} className="text-primary" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{method?.description}</p>
                  <div className="text-xs text-muted-foreground">Fee: {method?.fee}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Payout Request Form */}
      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Request Payout</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Input
              label="Payout Amount"
              type="number"
              placeholder="0.00"
              description={`Available: $${totalAvailable?.toLocaleString()}`}
            />
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Payout Method
              </label>
              <div className="text-sm text-muted-foreground">
                {payoutMethods?.find(m => m?.value === payoutMethod)?.label} - {payoutMethods?.find(m => m?.value === payoutMethod)?.description}
              </div>
            </div>

            <Input
              label="Notes (Optional)"
              type="text"
              placeholder="Add any special instructions..."
            />
          </div>

          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="font-medium text-foreground mb-3">Payout Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Requested Amount:</span>
                <span className="text-foreground">$0.00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Processing Fee:</span>
                <span className="text-foreground">$0.00</span>
              </div>
              <div className="border-t border-border pt-2 mt-2">
                <div className="flex justify-between font-medium">
                  <span className="text-foreground">You'll Receive:</span>
                  <span className="text-foreground">$0.00</span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Estimated arrival: {payoutMethods?.find(m => m?.value === payoutMethod)?.description}
              </div>
            </div>
            
            <Button variant="default" fullWidth className="mt-4">
              <Icon name="Send" size={16} className="mr-2" />
              Submit Payout Request
            </Button>
          </div>
        </div>
      </div>
      {/* Recent Payouts */}
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-1">Recent Payouts</h3>
            <p className="text-sm text-muted-foreground">Your payout history and status</p>
          </div>
          <Button variant="outline" size="sm">
            <Icon name="Download" size={16} className="mr-2" />
            Export History
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Amount</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Method</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Status</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payoutData?.recentPayouts?.map((payout) => (
                <tr key={payout?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="p-3">
                    <div className="text-sm text-foreground">{payout?.date}</div>
                    <div className="text-xs text-muted-foreground">{payout?.timeAgo}</div>
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-foreground">${payout?.amount?.toLocaleString()}</div>
                    {payout?.fee > 0 && (
                      <div className="text-xs text-muted-foreground">Fee: ${payout?.fee}</div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <Icon name={payoutMethods?.find(m => m?.value === payout?.method)?.icon || 'CreditCard'} size={16} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">
                        {payoutMethods?.find(m => m?.value === payout?.method)?.label || payout?.method}
                      </span>
                    </div>
                  </td>
                  <td className="p-3">
                    {getStatusBadge(payout?.status)}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm">
                        <Icon name="Eye" size={14} />
                      </Button>
                      <Button variant="ghost" size="sm">
                        <Icon name="Download" size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PayoutManagement;